import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type RegisteredUser = {
  _id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  createdAt?: string;
  updatedAt?: string;
};

type UsersApiResponse = {
  users?: RegisteredUser[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

const RegisteredUsers = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'updatedAt' | 'createdAt' | 'username' | 'email'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      limit,
      sortBy,
      sortOrder,
      role: 'customer',
    };

    if (search.trim()) {
      params.search = search.trim();
    }

    return params;
  }, [page, limit, sortBy, sortOrder, search]);

  const mapAndSetResponse = useCallback((payload: UsersApiResponse) => {
    const users = Array.isArray(payload?.users) ? payload.users : [];
    setRows(users);
    setTotal(typeof payload?.total === 'number' ? payload.total : users.length);
    setTotalPages(typeof payload?.totalPages === 'number' ? payload.totalPages : 0);
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!token) {
      setRows([]);
      setTotal(0);
      setTotalPages(0);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/users', {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
      });

      if (Array.isArray(response.data)) {
        const customerUsers = response.data.filter((user: RegisteredUser) => user.roles?.includes('customer'));
        setRows(customerUsers);
        setTotal(customerUsers.length);
        setTotalPages(0);
      } else {
        mapAndSetResponse(response.data as UsersApiResponse);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        try {
          const fallbackResponse = await axios.get('/api/v1/users', {
            headers: { Authorization: `Bearer ${token}` },
            params: queryParams,
          });

          if (Array.isArray(fallbackResponse.data)) {
            const customerUsers = fallbackResponse.data.filter((user: RegisteredUser) => user.roles?.includes('customer'));
            setRows(customerUsers);
            setTotal(customerUsers.length);
            setTotalPages(0);
          } else {
            mapAndSetResponse(fallbackResponse.data as UsersApiResponse);
          }
          return;
        } catch (fallbackErr: any) {
          setError(fallbackErr?.response?.data?.message || 'Failed to fetch registered users.');
          setRows([]);
          setTotal(0);
          setTotalPages(0);
          return;
        }
      }

      setError(err?.response?.data?.message || 'Failed to fetch registered users.');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [mapAndSetResponse, queryParams, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const canGoPrev = page > 1;
  const canGoNext = totalPages > 0 && page < totalPages;

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Registered Users</h4>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form
          className="d-flex gap-2 flex-grow-1"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput);
          }}
          style={{ minWidth: '260px' }}
        >
          <Form.Control
            placeholder="Search by username, email, name"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button type="submit" variant="outline-dark">Search</Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
          >
            Clear
          </Button>
        </Form>

        <Form.Select
          value={sortBy}
          onChange={(event) => {
            setPage(1);
            setSortBy(event.target.value as 'updatedAt' | 'createdAt' | 'username' | 'email');
          }}
          style={{ maxWidth: '180px' }}
        >
          <option value="updatedAt">Sort: Updated</option>
          <option value="createdAt">Sort: Created</option>
          <option value="username">Sort: Username</option>
          <option value="email">Sort: Email</option>
        </Form.Select>

        <Form.Select
          value={sortOrder}
          onChange={(event) => {
            setPage(1);
            setSortOrder(event.target.value as 'asc' | 'desc');
          }}
          style={{ maxWidth: '120px' }}
        >
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </Form.Select>

        <Form.Select
          value={limit}
          onChange={(event) => {
            setPage(1);
            setLimit(Number(event.target.value));
          }}
          style={{ maxWidth: '130px' }}
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </Form.Select>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Username</th>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Joined</th>
            <th>Last Updated</th>
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center">No registered users found.</td>
            </tr>
          ) : (
            rows.map((user, index) => {
              const roleLabel = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : 'customer';
              return (
                <tr key={user._id ?? `${user.email}-${index}`}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>{user.username || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>{`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-'}</td>
                  <td><Badge bg="info">{roleLabel}</Badge></td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : '-'}</td>
                  <td>{user.updatedAt ? new Date(user.updatedAt).toLocaleString() : '-'}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small>Showing {rows.length} of {total} registered users</small>
        <div className="d-flex align-items-center gap-2">
          <Button size="sm" variant="outline-secondary" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>
            Prev
          </Button>
          <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
          <Button size="sm" variant="outline-secondary" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default RegisteredUsers;
